import { FC, useCallback, useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { Guid, guid } from "odata-query";
import {
  IDepartmentResponse,
  IRoleListItemResponse,
  ITransferListItem,
} from "@miplatform/types";
import { TransferList } from "@mibuildweb/components";
import { useRoles } from "@mibuildweb/hooks";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IUserFormData } from "../../ConfigUsers.types";

export const UserRolesTabView: FC<IUserRolesTabProps> = ({
  form,
  extraTabProps: { departments, isReadOnly, selectedRoles = [] } = {},
}: IUserRolesTabProps) => {
  const { setValue } = form;

  const { getRoles, roles, setRoles } = useRoles();

  const mapRoleToDepartmentId = useCallback(
    (role: IRoleListItemResponse) => {
      const roleData = roles.find((r) => r.id === role.id);
      return {
        key: role.id,
        title: role.name,
        departmentId: roleData?.departmentId,
      };
    },
    [roles]
  );

  const availableRoles: ITransferListItem[] = useMemo(() => {
    return roles
      .map((role) => mapRoleToDepartmentId(role))
      .filter((role) => role !== null);
  }, [roles, mapRoleToDepartmentId]);

  const selectedRoleItems: ITransferListItem[] = useMemo(() => {
    return selectedRoles
      .map((role) => mapRoleToDepartmentId(role))
      .filter((role) => role !== null);
  }, [roles, selectedRoles, mapRoleToDepartmentId]);

  const handleGetRoles = async (departmentIds: Guid[]) => {
    const response = await getRoles({
      filters: { departmentId: { in: departmentIds } },
    });
    setRoles(response.data);
  };

  const handleAddItem = (key: string) => {
    const selectedRole = roles.find((d) => d.id === key);
    if (!selectedRole) {
      return;
    }
    const updatedRoles = [...selectedRoles, selectedRole];

    setValue("roles", updatedRoles, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveItem = (key: string) => {
    const updatedRoles = selectedRoles.filter((d) => d.id !== key);
    setValue("roles", updatedRoles, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    const deptIds = departments?.map((d) => guid(d.id));
    if (deptIds && deptIds.length > 0) {
      handleGetRoles(deptIds);
    }
  }, [departments]);

  return (
    <ConfigAccordionHeader title="Roles">
      <TransferList
        availableItems={availableRoles}
        selectedItems={selectedRoleItems}
        availableTitle="Available Roles"
        selectedTitle="Selected Roles"
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
        groupBy="departmentId"
        getGroupByInfo={(groupKey: string) => {
          const department = departments?.find((d) => d.id === groupKey);
          return {
            name: department?.name ?? "No Department Found",
            description: department?.description,
            key: groupKey,
          };
        }}
        disabled={isReadOnly}
      />
    </ConfigAccordionHeader>
  );
};

interface IUserRolesTabProps {
  form: UseFormReturn<IUserFormData>;
  extraTabProps?: {
    departments?: IDepartmentResponse[];
    isReadOnly?: boolean;
    selectedRoles?: IRoleListItemResponse[];
  };
}
